import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Box, Select, MenuItem, Snackbar, Alert, Link, Stack, Pagination, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

interface Purchase {
  id: number;
  user_id: number;
  user_email: string;
  transaction_type: string;
  status: string;
  amount: number;
  net_amount: number;
  payment_method: string | null;
  initiated_at: string;
  completed_at: string | null;
  description: string | null;
  payment_reference?: string | null;
  payment_metadata?: string | object;
}

interface PurchasesTableProps {
  tenantId?: number;
  limit?: number;
  transactionType?: string;
  showDepositsOnly?: boolean;
}

const PurchasesTable: React.FC<PurchasesTableProps> = ({ tenantId, limit = 100, transactionType, showDepositsOnly }) => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paginationData, setPaginationData] = useState({ total_pages: 1, total_items: 0 });
  const navigate = useNavigate();

  // Render a timestamp consistently in EST/EDT regardless of input nuances
  const formatEst = (iso: string | null) => {
    if (!iso) return '';
    // Ensure strings without explicit offset are treated as UTC
    const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: 'short', day: '2-digit',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short'
      }).format(new Date(normalized));
    } catch {
      // Fallback to raw display if parsing fails
      return new Date(iso).toString();
    }
  };

  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line
  }, [tenantId, page, pageSize]);

  const fetchPurchases = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      let url = showDepositsOnly
        ? `/api/v1/admin/deposits?page=${page}&page_size=${pageSize}`
        : `/api/v1/admin/purchases?page=${page}&page_size=${pageSize}`;
      if (tenantId) url += `&tenant_id=${tenantId}`;
      if (transactionType && !showDepositsOnly) url += `&transaction_type=${transactionType}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      
      console.log('[FRONTEND DEBUG] Fetching from URL:', url);
      console.log('[FRONTEND DEBUG] showDepositsOnly:', showDepositsOnly);
      console.log('[FRONTEND DEBUG] tenantId:', tenantId);
      
      const res = await api.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data as any;
      
      if (data.items && data.pagination) {
        setPurchases(data.items as Purchase[]);
        setPaginationData(data.pagination);
      } else if (Array.isArray(data)) {
        // Fallback for older API versions
        setPurchases(data as Purchase[]);
        setPaginationData({ total_pages: 1, total_items: data.length });
      }
    } catch (error) {
      console.error('[FRONTEND DEBUG] Error fetching purchases:', error);
      setError('Failed to fetch purchases');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/v1/admin/purchases/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
      fetchPurchases(); // Refresh the data
    } catch (error) {
      console.error('Failed to update status:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleEmailClick = (email: string) => {
    // Navigate to users page with search parameter
    navigate(`/users?search=${encodeURIComponent(email)}`);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Latest Purchases {tenantId ? '(Tenant)' : '(All Tenants)'} - Count: {paginationData.total_items || purchases.length}
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" onClick={() => { setPage(1); fetchPurchases(); }}>
          Search
        </Button>
      </Stack>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>User Email</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Net Amount</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Deposit Type</TableCell>
                <TableCell>Initiated At</TableCell>
                <TableCell>Completed At</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases.map((p) => {
                console.log('[FRONTEND DEBUG] Rendering purchase:', p);
                console.log('[FRONTEND DEBUG] transaction_type value:', p.transaction_type, 'type:', typeof p.transaction_type);
                let statusColor: string = '';
                if (p.status === 'success') statusColor = 'green';
                else if (p.status === 'pending') statusColor = 'orange';
                else if (p.status === 'failed') statusColor = 'red';
                let depositType = '-';
                try {
                  if (p.payment_metadata) {
                    const meta = typeof p.payment_metadata === 'string' ? JSON.parse(p.payment_metadata) : p.payment_metadata;
                    if (meta && meta.type) depositType = meta.type;
                  }
                } catch {}
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleEmailClick(p.user_email)}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {p.user_email}
                      </Link>
                    </TableCell>
                    <TableCell>{p.transaction_type || '-'}</TableCell>
                    <TableCell>
                      <Typography style={{ color: statusColor, fontWeight: 600 }}>
                        {p.status}
                      </Typography>
                    </TableCell>
                    <TableCell>{p.amount.toFixed(2)}</TableCell>
                    <TableCell>{p.net_amount.toFixed(2)}</TableCell>
                    <TableCell>{p.payment_method}</TableCell>
                    <TableCell>{depositType === 'LOYALTY' ? 'FREE PLAY' : depositType}</TableCell>
                    <TableCell>{formatEst(p.initiated_at)}</TableCell>
                    <TableCell>{formatEst(p.completed_at)}</TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell>{p.payment_reference || '-'}</TableCell>
                    <TableCell>
                      <Select 
                        value={p.status} 
                        onChange={e => handleStatusChange(p.id, e.target.value as string)} 
                        size="small"
                        disabled={p.status === 'success' || p.status === 'failed'}
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {paginationData.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination 
            count={paginationData.total_pages} 
            page={page} 
            onChange={(e, value) => setPage(value)} 
            color="primary" 
          />
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchasesTable; 